import { apiClient } from '@/lib/apiClient';

export interface UpcomingCall {
  id: string;
  title: string;
  description: string;
  event_type: string;
  start_datetime: string;
  end_datetime: string;
  location?: string;
  priority: string;
  status: string;
  related_lead_id?: number;
  related_contact_id?: number;
  created_at: string;
  updated_at: string;
}

class LeadActivitiesApiService {
  async getUpcomingCalls(limit: number = 5, userId?: number): Promise<UpcomingCall[]> {
    try {
      // Use user-specific endpoint if userId is provided, otherwise fallback to general endpoint
      const endpoint = userId 
        ? `/scheduler/upcoming/user/${userId}` 
        : `/scheduler/upcoming?limit=${limit}`;
      
      console.log('🗓️ Fetching upcoming calls from:', endpoint);
      const response = await apiClient.get<{ events: UpcomingCall[] }>(endpoint);
      return response.events || [];
    } catch (error) {
      console.error('Failed to fetch upcoming calls:', error);
      return [];
    }
  }

  async getUserActivities(userId: number, startDate?: string, endDate?: string, inReport?: boolean, limit?: number, offset?: number): Promise<UserActivity[]> {
    try {
      let endpoint = `/activity/user/${userId}`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (inReport) params.append('in_report', 'true');
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      console.log('📊 Fetching user activities from:', endpoint);
      const response = await apiClient.get<{ activities: UserActivity[] }>(endpoint);
      return response.activities || [];
    } catch (error) {
      console.error('Failed to fetch user activities:', error);
      return [];
    }
  }

  async getUserActivitiesPaginated(userId: number, startDate?: string, endDate?: string, limit: number = 10, offset: number = 0): Promise<UserActivity[]> {
    return this.getUserActivities(userId, startDate, endDate, false, limit, offset);
  }

  async getUserActivityStats(userId: number, startDate?: string, endDate?: string): Promise<ActivityStatsResponse | null> {
    try {
      let endpoint = `/activity/user/${userId}/stats`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      endpoint += `?${params.toString()}`;
      
      console.log('📊 Fetching activity stats from:', endpoint);
      const response = await apiClient.get<ActivityStatsResponse>(endpoint);
      return response || null;
    } catch (error) {
      console.error('Failed to fetch activity stats:', error);
      return null;
    }
  }

  async getUserActivitiesForReport(userId: number, startDate?: string, endDate?: string): Promise<UserActivity[]> {
    try {
      let endpoint = `/activity/user/${userId}`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('in_report', 'true');
      
      endpoint += `?${params.toString()}`;
      
      console.log('🎯 Fetching report activities from:', endpoint);
      const response = await apiClient.get<{ activities: UserActivity[] }>(endpoint);
      return response.activities || [];
    } catch (error) {
      console.error('Failed to fetch report activities:', error);
      return [];
    }
  }
}

export interface ActivityMetrics {
  total_activities: number;
  calls_attempted: number;
  calls_reached: number;
  emails_sent: number;
  sms_sent: number;
  other_activities: number;
  leads_contacted: number;
  us_activities: number;
  other_countries_activities: number;
  us_leads_contacted: number;
  other_countries_leads_contacted: number;
  leads_created: number;
  leads_assigned: number;
}

export interface DailyChartData {
  date: string;
  calls_attempted: number;
  calls_reached: number;
  emails_sent: number;
  sms_sent: number;
  other_activities: number;
}

export interface StageCount {
  current_stage: string;
  leads_contacted: number;
  activities: number;
}

export interface ActivityStatsResponse {
  activity_metrics: ActivityMetrics;
  daily_chart_data?: DailyChartData[];
  stage_counts?: StageCount[];
  filters_applied: {
    user_id: number;
    start_date: string;
    end_date: string;
    in_report: boolean | null;
  };
}

export interface UserActivity {
  id: number;
  lead_company_id: number;
  lead_contact_id?: number;
  activity_type: string;
  activity_status: string;
  activity_description: string;
  activity_date: string;
  schedule_date?: string;
  duration_minutes?: number;
  outcome?: string;
  ai_summary?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export const leadActivitiesApi = new LeadActivitiesApiService();